import * as React from 'react'

import { Cell, flexRender, RowData } from '@tanstack/react-table'

import { cn } from '../../utils/cn'
import { getAlignmentClass, getColumnMeta, getColumnStyle } from './columns'
import { STYLES } from './styles'

interface IBodyCellProps<TData extends RowData> {
  cell: Cell<TData, unknown>
  minRowHeight: number
  onCellClick?: (cell: Cell<TData, unknown>) => void
}

function getDefaultCellContent<TData extends RowData>(
  cell: Cell<TData, unknown>,
): React.ReactNode {
  if (cell.column.columnDef.cell !== undefined) {
    return flexRender(cell.column.columnDef.cell, cell.getContext())
  }

  if (cell.getIsPlaceholder()) {
    const value = cell.row.getValue(cell.column.id) as string | number | null

    return value == null ? null : String(value)
  }

  const value = cell.getValue()

  return value == null ? null : String(value)
}

function renderCellContent(content: React.ReactNode): React.ReactNode {
  if (typeof content !== 'string' && typeof content !== 'number') {
    return content
  }

  return (
    <span className="tw:block tw:min-w-0 tw:max-w-full tw:truncate">
      {content}
    </span>
  )
}

export default function BodyCell<TData extends RowData>({
  cell,
  minRowHeight,
  onCellClick,
}: IBodyCellProps<TData>): React.ReactElement {
  const meta = getColumnMeta(cell.column)
  const isPinned = !!cell.column.getIsPinned()
  const content = getDefaultCellContent(cell)

  return (
    <div
      role="gridcell"
      aria-colindex={cell.column.getIndex() + 1}
      className={cn(
        STYLES.bodyCell,
        isPinned && STYLES.bodyCellPinned,
        getAlignmentClass(meta.align),
        meta.cellClassName,
      )}
      style={{
        minHeight: minRowHeight,
        ...getColumnStyle(cell.column),
      }}
      onClick={
        onCellClick
          ? event => {
              event.stopPropagation()
              onCellClick(cell)
            }
          : undefined
      }
    >
      {renderCellContent(content)}
    </div>
  )
}
